import React, { useState } from 'react';
import { axiosInstance as axios } from '../../context/AuthContext';
import { FaPlus, FaVoteYea, FaLock } from 'react-icons/fa';
import { toast } from 'sonner';
import { useSubscription, FEATURES } from '../../hooks/useSubscription';
import UpgradeModal from '../UpgradeModal';

import { VoteModal } from './governance/VoteModal';
import { VotingCard } from './governance/VotingCard';
import { CreateVotingModal } from './governance/CreateVotingModal';

const API = '/api';

export function GovernanceTab({ familyId, votings, setVotings, roles, familyMembers, currentUser, showForm, setShowForm }) {
  const { hasFeature } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    vote_type: 'single_choice',
    options: ['', ''],
    allow_anonymous: false,
    requires_quorum: false,
    quorum_percentage: 50,
    end_date: ''
  });
  const [selectedVoting, setSelectedVoting] = useState(null);
  const [voting, setVoting] = useState(false);

  const handleCreateVoting = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const validOptions = formData.options.filter(opt => opt.trim());
      if (validOptions.length < 2) {
        alert('Please add at least 2 options');
        return;
      }

      const response = await axios.post(
        `${API}/votings`,
        {
          family_id: familyId,
          title: formData.title,
          description: formData.description,
          vote_type: formData.vote_type,
          options: validOptions,
          allow_anonymous: formData.allow_anonymous,
          requires_quorum: formData.requires_quorum,
          quorum_percentage: formData.quorum_percentage,
          end_date: formData.end_date || null
        },
        { withCredentials: true }
      );

      setVotings([response.data, ...votings]);
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        vote_type: 'single_choice',
        options: ['', ''],
        allow_anonymous: false,
        requires_quorum: false,
        quorum_percentage: 50,
        end_date: ''
      });
    } catch (error) {
      console.error('Error creating voting:', error);
      alert('Failed to create voting');
    } finally {
      setCreating(false);
    }
  };

  const handleCastVote = async (votingId, optionIds) => {
    setVoting(true);
    try {
      await axios.post(
        `${API}/votings/${votingId}/vote`,
        { option_ids: optionIds },
        { withCredentials: true }
      );

      setVotings(votings.map(v => {
        if (v.voting_id === votingId) {
          return {
            ...v,
            user_has_voted: true,
            user_vote: optionIds,
            total_votes: v.total_votes + 1,
            options: v.options.map(opt => ({
              ...opt,
              vote_count: optionIds.includes(opt.option_id) ? opt.vote_count + 1 : opt.vote_count
            }))
          };
        }
        return v;
      }));
      setSelectedVoting(null);

      toast.success('Vote recorded!', {
        description: 'Your vote has been submitted successfully.',
        duration: 4000,
      });
    } catch (error) {
      console.error('Error casting vote:', error);
      toast.error('Failed to cast vote', {
        description: error.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setVoting(false);
    }
  };

  const handleCloseVoting = async (votingId) => {
    if (!window.confirm('Are you sure you want to close this voting? No more votes can be cast.')) return;

    try {
      await axios.post(`${API}/votings/${votingId}/close`, {}, { withCredentials: true });
      setVotings(votings.map(v => v.voting_id === votingId ? { ...v, status: 'closed' } : v));
    } catch (error) {
      console.error('Error closing voting:', error);
      alert('Failed to close voting');
    }
  };

  const handleDeleteVoting = async (votingId) => {
    if (!window.confirm('Are you sure you want to delete this voting? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/votings/${votingId}`, { withCredentials: true });
      setVotings(votings.filter(v => v.voting_id !== votingId));
    } catch (error) {
      console.error('Error deleting voting:', error);
      alert('Failed to delete voting');
    }
  };

  const activeVotings = votings.filter(v => v.status === 'active');
  const closedVotings = votings.filter(v => v.status === 'closed');

  // Feature gate check
  if (!hasFeature(FEATURES.VOTING_GOVERNANCE)) {
    return (
      <div data-testid="governance-tab" className="space-y-6">
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="voting_governance"
          requiredTier="growing"
        />
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaVoteYea className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Voting & Governance</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create polls, make family decisions democratically, and manage governance with ease.
            Available on the Growing plan and above.
          </p>
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            🌿 Upgrade to Growing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="governance-tab" className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Family Governance</h2>
            <p className="text-gray-600">Create polls and make decisions together</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            data-testid="create-voting-btn"
          >
            <FaPlus />
            <span>Create Vote</span>
          </button>
        </div>
      </div>

      {/* Active Votings Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
          <FaVoteYea className="mr-2 text-green-600" />
          Active Votings ({activeVotings.length})
        </h3>

        {activeVotings.length === 0 ? (
          <div className="text-center py-8">
            <FaVoteYea className="text-gray-300 text-5xl mx-auto mb-4" />
            <p className="text-gray-600">No active votings. Create one to get started!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {activeVotings.map((v) => (
              <VotingCard
                key={v.voting_id}
                voting={v}
                currentUser={currentUser}
                onVote={() => setSelectedVoting(v)}
                onClose={() => handleCloseVoting(v.voting_id)}
                onDelete={() => handleDeleteVoting(v.voting_id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Closed Votings Section */}
      {closedVotings.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <FaLock className="mr-2 text-gray-500" />
            Closed Votings ({closedVotings.length})
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {closedVotings.map((v) => (
              <VotingCard
                key={v.voting_id}
                voting={v}
                currentUser={currentUser}
                onDelete={() => handleDeleteVoting(v.voting_id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Create Voting Modal */}
      {showForm && (
        <CreateVotingModal
          formData={formData}
          setFormData={setFormData}
          creating={creating}
          onSubmit={handleCreateVoting}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Vote Modal */}
      {selectedVoting && (
        <VoteModal
          voting={selectedVoting}
          onClose={() => setSelectedVoting(null)}
          onVote={handleCastVote}
          voting_in_progress={voting}
        />
      )}
    </div>
  );
}
